import React, { useRef, useState } from 'react';
import { Button } from '@view/components';
import {IGridFeatureConfig, IViewSettings, ObjectTypes} from '@data/models';
import styles from './styles.scss';
import { ConfigProvider } from '@data/providers';
import { ISceneData } from '@data/providers/SceneDataProvider';
import GridPanel from './GridPanel';
import { BooleanKeys } from '@types';
import { cn } from '@utils';
import { useCompanyColorScheme } from 'src/hooks/useCompanyColorScheme';

export type GridOption = {
  value: number | null;
  label: string;
};

type viewSettingsButton = {
  value: BooleanKeys<IViewSettings>;
  label: string;
  disabled?: (sceneData?: ISceneData) => boolean;
  disabledHint?: string;
  onChange?: (settingEnabled: boolean, setViewSetting: (setting: keyof IViewSettings, value: any) => void) => void;
  hidden?: (configProvider: ConfigProvider, sceneData?: ISceneData) => boolean;
  active?: (viewSetting: boolean | undefined) => boolean;
};

interface IViewSettingsProps {
  configProvider: ConfigProvider;
  viewSettings: IViewSettings;
  toggleViewSetting: (setting: BooleanKeys<IViewSettings>) => void;
  className?: string;
  sceneData?: ISceneData;
  setViewSetting: (setting: keyof IViewSettings, value: any) => void;
  companyColorScheme?: ReturnType<typeof useCompanyColorScheme>[0];
}

const settings: viewSettingsButton[] = [
  {
    value: 'overlay',
    label: 'Overlay',
    hidden: configProvider=> !configProvider.isFeatureEnabled("overlay")
  },
  { value: 'attachments', label: 'Attachments' },
  { value: 'biteRamps', label: 'Bite ramps' },
  { value: 'elastics', label: 'Elastics' },
  { value: 'pontics', label: 'Pontics' },
  { value: 'iprs', label: 'Separation' },
  {
    value: 'occlusions',
    label: 'Contacts',
    hidden: (_, sceneData) => !sceneData?.occlusions.length
  },
  {
    value: 'hideGum',
    label: 'Gum',
    active: value => !value,
    hidden: configProvider => !configProvider.isFeatureEnabled('hideGum')
  },
  {
    value: 'showCbctJaws',
    label: 'Bones',
    disabled: (sceneData?: ISceneData) => !Object.values(sceneData?.allObjects || {}).some(obj => obj.type === ObjectTypes.CbctJaw),
    disabledHint: "Bones are not available",
    onChange: (showCbctJaws, setViewSetting) => {
      if (showCbctJaws) setViewSetting("hideGum", true);
    }
  },
];

const ViewSettings = ({
  configProvider,
  viewSettings,
  toggleViewSetting,
  className,
  sceneData,
  companyColorScheme,
  setViewSetting,
}: IViewSettingsProps) => {
  const [gridPanelOpen, setGridPanelState] = useState<boolean>(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gridOptions: GridOption[] = [{ value: null, label: 'Grid off' }];

  const gridFeatureConfig =
    configProvider.getFeatureConfig<IGridFeatureConfig>('grid');

  if (gridFeatureConfig?.enabled) {
    gridFeatureConfig.sizes.forEach((size: number) => {
      gridOptions.push({ value: size, label: `${size} mm` });
    });
  }

  const handleGridSwitcherMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setGridPanelState(true);
  };

  const handleGridSwitcherMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setGridPanelState(false);
    }, 600);
  };

  const handleGridPanelClick = (gridSize: number | null) => {
    if (gridSize) {
      setViewSetting('grid', true);
      setViewSetting('gridSize', gridSize);
    } else {
      setViewSetting('grid', false);
    }
  };

  const handleGridButtonClick = () => {
    const currentSelectedOption = !viewSettings.grid
      ? null
      : viewSettings.gridSize;
    // Grid options from 'gridFeatureConfig'
    const options = gridOptions.map((v) => v.value);
    // Determining and switching to next grid option
    const index = options.indexOf(currentSelectedOption);
    const newOption = options[(index + 1) % options.length];
    handleGridPanelClick(newOption);
  };

  return (
    <div className={cn(styles.viewSettingsBar_group, className)}>
      <div className="relative">
        {gridFeatureConfig?.enabled && (
          <Button
            active={viewSettings.grid || gridPanelOpen}
            className={cn(styles.viewSettingsBar_button, 'min-h-full')}
            onMouseEnter={handleGridSwitcherMouseEnter}
            onMouseLeave={handleGridSwitcherMouseLeave}
            onClick={handleGridButtonClick}
          >
            <img
              src="/assets/images/grid_icon.svg"
              className={cn(
                companyColorScheme?.light && 'invert-[.75] drop-shadow-lg'
              )}
              alt="View Settings"
            />
          </Button>
        )}

        {gridPanelOpen && (
          <GridPanel
            onClose={() => {
              setGridPanelState(false);
            }}
            onMouseEnter={handleGridSwitcherMouseEnter}
            onMouseLeave={handleGridSwitcherMouseLeave}
            gridState={viewSettings.grid}
            gridSize={viewSettings.gridSize}
            gridOptions={gridOptions}
            onGridPanelClick={handleGridPanelClick}
          />
        )}
      </div>
      {settings
        .filter(setting => !setting.hidden?.(configProvider, sceneData))
        .map((setting) => {
          const isDisabled = setting.disabled?.(sceneData);

          return (
            <Button
              key={setting.value}
              tooltip={isDisabled ? setting.disabledHint : setting.label}
              active={setting.active ? setting.active(viewSettings?.[setting.value]) : viewSettings?.[setting.value]}
              className={isDisabled ? cn(styles.viewSettingsBar_button__disabled) : cn(styles.viewSettingsBar_button)}
              onClick={() => {
                if (isDisabled) return;
                if (setting.onChange) {
                  const newViewSetting: boolean = !viewSettings?.[setting.value];
                  setting.onChange(newViewSetting, setViewSetting);
                }
                toggleViewSetting(setting.value);
              }}
            >
              <img
                src={`/assets/images/${setting.value}_icon.svg`}
                className={cn(
                  companyColorScheme?.light && 'invert-[.75] drop-shadow-lg'
                )}
                alt="View Settings"
              />
            </Button>
          );
        })}
    </div>
  );
};

export default ViewSettings;
